import React, {forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {css, Global} from '@emotion/react'
import ReactDatePicker from 'react-datepicker'
import reactDatePickerCss from 'react-datepicker/dist/react-datepicker.css'
import {useIntl} from 'react-intl'
import {Input, useMergeRefs} from '@chakra-ui/react'
import useDateFnsLocale from '../../hooks/use-date-fns-locale'

const CustomDatePickerInput = forwardRef(({inputRef, ...props}, ref) => {
    const combinedRef = useMergeRefs(ref, inputRef)
    return <Input ref={combinedRef} {...props} />
})

if (process.env.NODE_ENV !== 'production') {
    CustomDatePickerInput.displayName = 'CustomDatePickerInput'
}

CustomDatePickerInput.propTypes = {
    inputRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
}

const DatePicker = forwardRef(
    ({inputProps, placeholder, value, onBlur, onChange, onFocus}, ref) => {
        const {formatDate} = useIntl()

        const {data: dateFnsLocale} = useDateFnsLocale()

        // memoize to prevent creating new date instances on every render
        const selected = useMemo(() => (value ? new Date(value) : null), [value])

        // extract the date format from react-intl using a hack from https://stackoverflow.com/a/52498569
        const dateFormat = useMemo(() => {
            const isoString = '3333-02-01'

            const intlString = formatDate(isoString)

            return intlString
                .replace('01', 'dd')
                .replace('1', 'd')
                .replace('02', 'MM')
                .replace('2', 'M')
                .replace('3333', 'yyyy')
        }, [formatDate])

        return (
            <>
                <Global
                    styles={css`
                        ${reactDatePickerCss}

                        .react-datepicker-wrapper {
                            width: 100%;
                        }
                    `}
                />
                <ReactDatePicker
                    customInput={<CustomDatePickerInput ref={ref} {...inputProps} />}
                    customInputRef="inputRef"
                    dateFormat={dateFormat}
                    locale={dateFnsLocale}
                    placeholderText={placeholder}
                    selected={selected}
                    showMonthYearDropdown
                    onBlur={onBlur}
                    onChange={(date) => onChange?.(date?.toISOString().split('T')[0])}
                    onFocus={onFocus}
                />
            </>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    DatePicker.displayName = 'DatePicker'
}

DatePicker.propTypes = {
    inputProps: PropTypes.object,
    placeholder: PropTypes.string,
    value: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func
}

export default DatePicker
